/* eslint-disable no-unused-vars */
import React, { useMemo } from 'react';
import { NM, CURRENCIES } from '../utils/constants';
import { mlSafe, fu } from '../utils/format';
import { calcEmployeeMonthlyCosts, defaultEmployee } from '../hooks/usePeopleCosts';

const MN = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function SalaryInput({ value, onChange, disabled }) {
  const [focused, setFocused] = React.useState(false);
  const display = focused
    ? (value || '')
    : (parseFloat(value) > 0 ? Math.round(parseFloat(value)).toLocaleString('en-US') : (value || ''));
  return (
    <input
      type={focused ? 'number' : 'text'}
      value={display} disabled={disabled}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={e => onChange(e.target.value)}
      style={{ background: 'transparent', border: 'none', outline: 'none',
        color: 'var(--t1)', fontSize: 12, width: 100, textAlign: 'right' }}
    />
  );
}


const YEARS = Array.from({ length: 12 }, (_, i) => new Date().getFullYear() - 1 + i);

function MonthYearPicker({ value, onChange, label, disabled }) {
  // Convert stored { day, month, year } to ISO date string for input[type=date]
  const toISO = (v) => {
    if (!v?.year || v?.month === '' || v?.month === undefined) return '';
    const y = String(v.year).padStart(4, '0');
    const m = String(parseInt(v.month) + 1).padStart(2, '0');
    const d = String(v.day || 1).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  const fromISO = (iso) => {
    if (!iso) return { month: '', year: '', day: '' };
    const [y, m, d] = iso.split('-');
    return { year: y, month: String(parseInt(m) - 1), day: String(parseInt(d)) };
  };
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      {label && <span style={{ fontSize: 9, color: 'var(--t3)', minWidth: 30 }}>{label}</span>}
      <input type="date" disabled={disabled}
        value={toISO(value)}
        onChange={e => onChange(fromISO(e.target.value))}
        style={{ fontSize: 10, padding: '3px 5px', background: 'var(--s2)',
          border: '1px solid var(--e1)', borderRadius: 'var(--r)', color: 'var(--t1)', outline: 'none',
          colorScheme: 'dark' }}
      />
    </div>
  );
}

export default function PeoplePanel({
  employees, departments, oncostsPct, payRisePct, payRiseMonth,
  updateEmployees, updateDepartments, updateOncosts, updatePayRise,
  startDate, functionalCurrency, convertFrom, readOnly = false,
  plActuals, plMappings, currentMonthIdx,
  bonusVals: bonusValsProp = null, unreconVals: unreconValsProp = null,
  onUpdateBonus, onUpdateUnrecon,
}) {
  const fc = functionalCurrency || 'USD';
  const bonusVals   = bonusValsProp  != null ? bonusValsProp  : Array(NM).fill('');
  const unreconDiff = unreconValsProp != null ? unreconValsProp : Array(NM).fill('');

  const addEmployee = () => {
    const emp = defaultEmployee();
    emp.currency = fc;
    updateEmployees([...employees, emp]);
  };

  const updateEmp = (id, field, value) => {
    updateEmployees(employees.map(e => e.id === id ? { ...e, [field]: value } : e));
  };

  const removeEmp = (id) => {
    if (!window.confirm('Remove this employee?')) return;
    updateEmployees(employees.filter(e => e.id !== id));
  };

  // Calculate monthly costs in FC for each employee
  const empCosts = useMemo(() => {
    return employees.map(emp => {
      const localCosts = calcEmployeeMonthlyCosts(emp, oncostsPct, payRisePct, payRiseMonth, startDate);
      const empCurrency = emp.currency || fc;
      const fcCosts = localCosts.map((v, i) =>
        convertFrom ? convertFrom(v, empCurrency, fc, i) : v
      );
      return { id: emp.id, local: localCosts, fc: fcCosts };
    });
  }, [employees, oncostsPct, payRisePct, payRiseMonth, startDate, fc, convertFrom]);

  // Totals by department type
  const { devTotal, ohTotal } = useMemo(() => {
    const devTotal = Array(NM).fill(0);
    const ohTotal  = Array(NM).fill(0);
    employees.forEach((emp, i) => {
      const dept = departments.find(d => d.name === emp.department);
      const isdev = dept?.type === 'developer';
      empCosts[i]?.fc.forEach((v, mi) => {
        if (isdev) devTotal[mi] += v;
        else ohTotal[mi] += v;
      });
    });
    return { devTotal, ohTotal };
  }, [employees, departments, empCosts]);

  const annualTotal = (arr) => arr.slice(0, 12).reduce((a, v) => a + (v || 0), 0); // first 12 months = annual

  return (
    <div className="panel">

      {/* Settings bar */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head"><span className="card-title">Payroll Settings</span></div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <div className="d-sec-lbl">Employee Oncosts %</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                <input type="number" step="0.1" min="0" max="100"
                  value={oncostsPct} disabled={readOnly}
                  onChange={e => updateOncosts(e.target.value)}
                  style={{ width: 70, padding: '5px 8px', fontSize: 12, background: 'var(--s2)',
                    border: '1px solid var(--e1)', borderRadius: 'var(--r)', color: 'var(--t1)', outline: 'none' }} />
                <span style={{ fontSize: 11, color: 'var(--t3)' }}>%</span>
                <span style={{ fontSize: 10, color: 'var(--t3)' }}>Applies to employees only, not EoR</span>
              </div>
            </div>
            <div>
              <div className="d-sec-lbl">Annual Pay Rise</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                <input type="number" step="0.1" min="0" max="100"
                  value={payRisePct} disabled={readOnly}
                  onChange={e => updatePayRise(e.target.value, payRiseMonth)}
                  style={{ width: 60, padding: '5px 8px', fontSize: 12, background: 'var(--s2)',
                    border: '1px solid var(--e1)', borderRadius: 'var(--r)', color: 'var(--t1)', outline: 'none' }} />
                <span style={{ fontSize: 11, color: 'var(--t3)' }}>% from</span>
                <select value={payRiseMonth ?? ''} disabled={readOnly}
                  onChange={e => updatePayRise(payRisePct, e.target.value !== '' ? parseInt(e.target.value) : '')}
                  style={{ fontSize: 11, padding: '4px 20px 4px 8px' }}>
                  <option value="">— month —</option>
                  {Array.from({ length: NM }, (_, i) => (
                    <option key={i} value={i}>{mlSafe(i, startDate)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Employee roster */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-head">
          <span className="card-title">Employee List</span>
          <span className="card-meta">{employees.length} people</span>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
              <thead>
                <tr style={{ background: 'var(--s1)' }}>
                  {['Name','Role','Department','Status','Annual Salary','Currency','Salary ('+fc+')','Start Date','End Date',''].map((h, i) => (
                    <th key={i} style={{ padding: '6px 10px', fontSize: 8, letterSpacing: '.1em',
                      textTransform: 'uppercase', color: 'var(--t4)', fontWeight: 400,
                      textAlign: 'left', borderBottom: '1px solid var(--e0)', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.map((emp, idx) => {
                  const costs = empCosts[idx];
                  const annFC = annualTotal(costs?.fc || []);
                  const dept = departments.find(d => d.name === emp.department);
                  return (
                    <tr key={emp.id} style={{ borderBottom: '1px solid var(--e0)', background: idx % 2 === 0 ? 'var(--s0)' : 'var(--s1)' }}>
                      <td style={{ padding: '6px 10px' }}>
                        <input value={emp.name} disabled={readOnly} placeholder="Name"
                          onChange={e => updateEmp(emp.id, 'name', e.target.value)}
                          style={{ background: 'transparent', border: 'none', outline: 'none',
                            color: 'var(--t0)', fontSize: 12, width: 120 }} />
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <input value={emp.role} disabled={readOnly} placeholder="Role"
                          onChange={e => updateEmp(emp.id, 'role', e.target.value)}
                          style={{ background: 'transparent', border: 'none', outline: 'none',
                            color: 'var(--t1)', fontSize: 12, width: 120 }} />
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <select value={emp.department} disabled={readOnly}
                          onChange={e => updateEmp(emp.id, 'department', e.target.value)}
                          style={{ fontSize: 11, padding: '3px 20px 3px 6px', maxWidth: 140 }}>
                          <option value="">— dept —</option>
                          {departments.map(d => (
                            <option key={d.id} value={d.name}>{d.name} ({d.type})</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <select value={emp.status} disabled={readOnly}
                          onChange={e => updateEmp(emp.id, 'status', e.target.value)}
                          style={{ fontSize: 11, padding: '3px 20px 3px 6px' }}>
                          <option value="employee">Employee</option>
                          <option value="eor">EoR</option>
                        </select>
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <SalaryInput value={emp.salary} disabled={readOnly}
                          onChange={v => updateEmp(emp.id, 'salary', v)} />
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <select value={emp.currency || fc} disabled={readOnly}
                          onChange={e => updateEmp(emp.id, 'currency', e.target.value)}
                          style={{ fontSize: 11, padding: '3px 20px 3px 6px' }}>
                          {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '6px 10px', color: 'var(--t1)', fontSize: 12, textAlign: 'right' }}>
                        {annFC > 0 ? fu(annFC) : '—'}
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <MonthYearPicker value={emp.startDate} disabled={readOnly}
                          onChange={v => updateEmp(emp.id, 'startDate', v)} />
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <MonthYearPicker value={emp.endDate} disabled={readOnly}
                          onChange={v => updateEmp(emp.id, 'endDate', v)} />
                      </td>
                      <td style={{ padding: '6px 10px' }}>
                        <button className="rm" disabled={readOnly} onClick={() => removeEmp(emp.id)}>×</button>
                      </td>
                    </tr>
                  );
                })}
                {employees.length === 0 && (
                  <tr><td colSpan={10} className="nomsg">No employees added yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {!readOnly && (
            <div style={{ padding: '10px 16px' }}>
              <button className="add-g" onClick={addEmployee}>+ Add Employee</button>
            </div>
          )}
        </div>
      </div>

      {/* Monthly cost summary */}
      <div className="card">
        <div className="card-head">
          <span className="card-title">Monthly Payroll Cost ({fc})</span>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="tw">
            <table className="dt">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', minWidth: 160 }}>Employee</th>
                  {Array.from({ length: NM }, (_, i) => <th key={i}>{mlSafe(i, startDate)}</th>)}
                </tr>
              </thead>
              <tbody>
                {employees.map((emp, idx) => {
                  const costs = empCosts[idx];
                  if (!costs?.fc.some(v => v > 0)) return null;
                  const dept = departments.find(d => d.name === emp.department);
                  return (
                    <tr key={emp.id} className="rc">
                      <td>
                        <span>{emp.name || '(unnamed)'}</span>
                        {dept && <span style={{ fontSize: 8, color: 'var(--t4)', marginLeft: 6 }}>{dept.type}</span>}
                      </td>
                      {costs.fc.map((v, i) => <td key={i}>{v > 0 ? fu(v) : '—'}</td>)}
                    </tr>
                  );
                })}
                {devTotal.some(v => v > 0) && (
                  <tr className="rt">
                    <td>Total Developer Payroll</td>
                    {devTotal.map((v, i) => <td key={i}>{v > 0 ? fu(v) : '—'}</td>)}
                  </tr>
                )}
                {ohTotal.some(v => v > 0) && (
                  <tr className="rt">
                    <td>Total Overhead Payroll</td>
                    {ohTotal.map((v, i) => <td key={i}>{v > 0 ? fu(v) : '—'}</td>)}
                  </tr>
                )}
                <tr className="rg">
                  <td>Total Payroll</td>
                  {Array.from({ length: NM }, (_, i) => (
                    <td key={i}>{(devTotal[i] + ohTotal[i]) > 0 ? fu(devTotal[i] + ohTotal[i]) : '—'}</td>
                  ))}
                </tr>
                {/* Salary Reconciliation */}
                {(() => {
                  const unreconRow = Array.from({ length: NM }, (_, i) => {
                    const isLocked = currentMonthIdx !== null && currentMonthIdx !== undefined && i <= currentMonthIdx;
                    if (isLocked && startDate && plActuals && plMappings) {
                      // Sum all accounts mapped to salaries/contractors/other_staff
                      const totalMonths = startDate.month + i;
                      const yr = startDate.year + Math.floor(totalMonths / 12);
                      const mo = ((totalMonths % 12) + 12) % 12;
                      const mk = `${yr}-${String(mo + 1).padStart(2, '0')}`;
                      let mapped = 0;
                      Object.entries(plActuals[mk] || {}).forEach(([acc, val]) => {
                        const m = plMappings[acc];
                        if (['salaries','contractors','other_staff'].includes(m?.type)) {
                          mapped += parseFloat(val) || 0;
                        }
                      });
                      const payroll = (devTotal[i] + ohTotal[i]);
                      return { isLocked, mapped, payroll, diff: mapped - payroll };
                    }
                    // Forecast months — manual unreconciled diff
                    return { isLocked: false, payroll: devTotal[i] + ohTotal[i], diff: parseFloat(unreconDiff[i]) || 0 };
                  });

                  const hasSalaryData = unreconRow.some(r => r.isLocked && r.mapped > 0);
                  if (!hasSalaryData && unreconRow.every(r => !r.isLocked)) return null;

                  return (
                    <>
                      <tr className="ri">
                        <td>Bonus</td>
                        {Array.from({ length: NM }, (_, i) => (
                          <td key={i}>
                            <input type="number" className="ci"
                              value={bonusVals[i] || ''}
                              onChange={e => {
                                const next = [...bonusVals];
                                next[i] = e.target.value;
                                onUpdateBonus?.(next);
                              }}
                              placeholder="0" />
                          </td>
                        ))}
                      </tr>
                      <tr className="rc">
                        <td>Unreconciled Difference</td>
                        {unreconRow.map((r, i) => (
                          <td key={i}>
                            {r.isLocked ? (
                              <span style={{ color: Math.abs(r.diff) < 1 ? 'var(--t3)' : r.diff > 0 ? 'var(--amber)' : 'var(--red)', fontSize: 10 }}>
                                {Math.abs(r.diff) < 1 ? '—' : fu(r.diff)}
                              </span>
                            ) : (
                              <input type="number" className="ci"
                                value={unreconDiff[i] || ''}
                                onChange={e => {
                                  const next = [...unreconDiff];
                                  next[i] = e.target.value;
                                  onUpdateUnrecon?.(next);
                                }}
                                placeholder="0" />
                            )}
                          </td>
                        ))}
                      </tr>
                      <tr className="rg">
                        <td>Total Salary Costs</td>
                        {unreconRow.map((r, i) => (
                          <td key={i}>{fu((r.isLocked ? r.mapped : r.payroll + r.diff) + (parseFloat(bonusVals[i]) || 0))}</td>
                        ))}
                      </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// Export monthly totals for use in P&L
export function usePeopleMonthlyTotals(employees, departments, oncostsPct, payRisePct, payRiseMonth, startDate, functionalCurrency, convertFrom) {
  const fc = functionalCurrency || 'USD';
    return useMemo(() => {
    const devTotal = Array(NM).fill(0);
    const ohTotal  = Array(NM).fill(0);
    employees.forEach(emp => {
      const localCosts = calcEmployeeMonthlyCosts(emp, oncostsPct, payRisePct, payRiseMonth, startDate);
      const empCcy = emp.currency || fc;
      const fcCosts = localCosts.map((v, i) => convertFrom ? convertFrom(v, empCcy, fc, i) : v);
      const dept = departments.find(d => d.name === emp.department);
      const isdev = dept?.type === 'developer';
      fcCosts.forEach((v, i) => { if (isdev) devTotal[i] += v; else ohTotal[i] += v; });
    });
    return { devTotal, ohTotal, total: Array.from({ length: NM }, (_, i) => devTotal[i] + ohTotal[i]) };
  }, [employees, departments, oncostsPct, payRisePct, payRiseMonth, startDate, fc, convertFrom]);
}
